import type { OrderWarehouseSummary } from '../types/orderWarehouseSummary';
import { reportDtoAnomaly } from './common';

const isAmount = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** A failed or redacted read must never look like zero stock or zero cost. */
export function normalizeOrderWarehouseSummary(raw: unknown): OrderWarehouseSummary {
  const value = raw as OrderWarehouseSummary | null;
  const invalid = () => {
    reportDtoAnomaly('OrderWarehouseSummary', 'payload', raw);
    throw new Error('Invalid warehouse summary');
  };
  if (!value || !Array.isArray(value.lines) || !Array.isArray(value.documents)) return invalid();
  if (value.lines.some(line => !line || typeof line.id !== 'string' || typeof line.name !== 'string'
    || typeof line.unit !== 'string'
    || !isAmount(line.orderedQuantity)
    || ![line.availableQuantity, line.issuedQuantity, line.remainingQuantity, line.invoicedQuantity].every(amount => amount === null || isAmount(amount)))) return invalid();
  if (value.documents.some(document => !document || typeof document.id !== 'string'
    || typeof document.wzNumber !== 'string' || typeof document.status !== 'string')) return invalid();
  if (value.finance != null && (![value.finance.knownFifoCostPLN, value.finance.transportCostPLN, value.finance.otherCostPLN].every(isAmount)
    || typeof value.finance.costPending !== 'boolean')) return invalid();
  const plan = value.finance?.plan;
  if (plan != null && (!(['averageAccepted', 'usesAverage', 'costPending'] as const).every(key => typeof plan[key] === 'boolean')
    || !isAmount(plan.averageCostPLN) || !isAmount(plan.stockCostPLN)
    || ![plan.candidateCostPLN, plan.totalCostPLN, plan.marginPLN, plan.marginPercent].every(amount => amount === null || isAmount(amount))
    || !Array.isArray(plan.lines) || plan.lines.some(line => !line || typeof line.id !== 'string' || typeof line.costPending !== 'boolean'
      || ![line.averagePurchasePricePLN, line.averageCostPLN].every(amount => amount === null || isAmount(amount))
      || (line.averagePurchasePriceUnit !== null && !['pcs', 'm2', 'm3'].includes(line.averagePurchasePriceUnit))))) return invalid();
  return {
    lines: value.lines,
    documents: value.documents,
    ...(value.finance != null ? { finance: value.finance } : {}),
  };
}
